import { Component, type ReactNode, type ErrorInfo } from "react";
import { AuthContext, useAuthProvider } from "./hooks/useAuth";
import { AppContent } from "./AppContent";
import { DevShowcase } from "./DevShowcase";
import "./styles.css";

const IS_DEV_SHOWCASE =
  import.meta.env.DEV && window.location.hash === "#dev";

interface ErrorBoundaryState {
  error: Error | null;
}

class ErrorBoundary extends Component<
  { children: ReactNode },
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("App crashed:", error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen items-center justify-center p-4">
          <div className="text-center">
            <p className="text-sm text-red-600">
              Something went wrong. Please reload.
            </p>
            <p className="mt-2 text-xs text-gray-500">
              {this.state.error.message}
            </p>
            <button
              className="mt-4 rounded-md bg-blue-500 px-4 py-2 text-sm text-white"
              onClick={() => window.location.reload()}
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export function App() {
  const auth = useAuthProvider();

  if (IS_DEV_SHOWCASE) {
    return (
      <ErrorBoundary>
        <AuthContext.Provider value={auth}>
          <DevShowcase />
        </AuthContext.Provider>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <AuthContext.Provider value={auth}>
        <div className="p-4">
          <AppContent />
        </div>
      </AuthContext.Provider>
    </ErrorBoundary>
  );
}
